import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SignedIn, SignedOut, UserButton } from '@clerk/clerk-react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import Sidebar from './components/Sidebar';
import Header from './components/Header';
import AuthScreen from './components/AuthScreen';
import LandingPage from './components/LandingPage';
import WalletManagement from './components/WalletManagement';
import TradingOperations from './components/TradingOperations';
import TokenOperations from './components/TokenOperations';
import Recovery from './components/Recovery';
import Refund from './components/Refund';
import AdminPanel from './components/AdminPanel';
import AdminShell from './components/AdminShell';
import TokenDistributionCalculator from './components/TokenDistributionCalculator';
import LaunchWizard from './components/LaunchWizard';
import LpLocker from './components/LpLocker';
import { useWireEthereumAuth } from './auth/wireEthereumAuth';
import ProjectsDashboard from './projects/ProjectsDashboard';
import { useProject } from './projects/ProjectContext';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#a855f7',
      light: '#c084fc',
      dark: '#6d28d9',
    },
    secondary: {
      main: '#22d3ee',
    },
    success: {
      main: '#22c55e',
    },
    background: {
      default: '#05060a',
      paper: 'rgba(13, 15, 24, 0.72)',
    },
    text: {
      primary: '#e9e7ff',
      secondary: 'rgba(233, 231, 255, 0.72)',
    },
    divider: 'rgba(168, 85, 247, 0.18)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#05060a',
          backgroundImage:
            'radial-gradient(1200px 800px at 15% 10%, rgba(168, 85, 247, 0.18) 0%, rgba(0,0,0,0) 60%), radial-gradient(1000px 600px at 85% 20%, rgba(34, 211, 238, 0.10) 0%, rgba(0,0,0,0) 55%), radial-gradient(900px 600px at 50% 110%, rgba(168, 85, 247, 0.10) 0%, rgba(0,0,0,0) 55%)',
        },
        '*::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(168, 85, 247, 0.35)',
          borderRadius: 999,
          border: '2px solid rgba(5, 6, 10, 0.9)',
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(255,255,255,0.04)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
        },
        containedPrimary: {
          backgroundImage: 'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
          boxShadow: '0 10px 30px rgba(168, 85, 247, 0.25)',
        },
        outlined: {
          borderColor: 'rgba(168, 85, 247, 0.35)',
          backgroundColor: 'rgba(168, 85, 247, 0.06)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(168, 85, 247, 0.16)',
          backgroundImage:
            'linear-gradient(180deg, rgba(13, 15, 24, 0.78) 0%, rgba(8, 9, 14, 0.78) 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 18px 60px rgba(0, 0, 0, 0.55)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'rgba(255,255,255,0.04)',
        },
        notchedOutline: {
          borderColor: 'rgba(168, 85, 247, 0.22)',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(168, 85, 247, 0.14)',
          backgroundColor: 'rgba(10, 12, 18, 0.55)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: 'rgba(233, 231, 255, 0.82)',
          fontWeight: 700,
          borderBottom: '1px solid rgba(168, 85, 247, 0.18)',
          backgroundColor: 'rgba(13, 15, 24, 0.75)',
        },
        body: {
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(168, 85, 247, 0.06)',
          },
        },
      },
    },
  },
});

function App() {
  const [signedOutView, setSignedOutView] = useState({ screen: 'landing', authMode: 'signIn' }); // landing | auth
  const { currentProjectId, setCurrentProjectId } = useProject();
  useWireEthereumAuth();

  const SignedInRoutes = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const pathToSection = (pathname) => {
      if (pathname.startsWith('/launch-wizard')) return 'launchWizard';
      if (pathname.startsWith('/launch-planner')) return 'distribution';
      if (pathname.startsWith('/wallets')) return 'wallet';
      if (pathname.startsWith('/bundle-launch')) return 'trading';
      if (pathname.startsWith('/lp-locker')) return 'lpLocker';
      if (pathname.startsWith('/token-operations')) return 'token';
      if (pathname.startsWith('/backup')) return 'recovery';
      if (pathname.startsWith('/refund')) return 'refund';
      if (pathname.startsWith('/admin')) return 'admin';
      if (pathname.startsWith('/projects')) return 'projects';
      return 'wallet';
    };

    const sectionToPath = (sectionId) => {
      switch (sectionId) {
        case 'launchWizard':
          return '/launch-wizard';
        case 'distribution':
          return '/launch-planner';
        case 'wallet':
          return '/wallets';
        case 'trading':
          return '/bundle-launch';
        case 'lpLocker':
          return '/lp-locker';
        case 'token':
          return '/token-operations';
        case 'recovery':
          return '/backup';
        case 'refund':
          return '/refund';
        case 'admin':
          return '/admin';
        default:
          return '/wallets';
      }
    };

    const selectedSection = pathToSection(location.pathname);

    // If we don't have a project selected yet, force Projects dashboard.
    if (!currentProjectId && location.pathname !== '/projects' && !location.pathname.startsWith('/admin')) {
      return <Navigate to="/projects" replace />;
    }

    if (location.pathname.startsWith('/admin')) {
      return (
        <AdminShell>
          <AdminPanel />
        </AdminShell>
      );
    }

    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {location.pathname !== '/projects' ? (
          <Sidebar
            selectedSection={selectedSection}
            onSectionChange={(id) => navigate(sectionToPath(id))}
            onLogoClick={() => navigate('/projects')}
          />
        ) : null}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Header
            title="EVM Bundler Manager"
            showTitle={false}
            showLogo={location.pathname === '/projects'}
            logoSize="clamp(34px, 4.5vw, 52px)"
            onLogoClick={() => navigate('/projects')}
            logoContainerSx={location.pathname === '/projects' ? { maxWidth: 264 } : undefined}
            actions={
              <UserButton
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    userButtonTrigger: {
                      border: '1px solid rgba(255,255,255,0.12)',
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      padding: '6px 10px',
                      borderRadius: '999px',
                    },
                    userButtonPopoverCard: {
                      backgroundColor: '#0b0c12',
                      border: '1px solid rgba(168, 85, 247, 0.20)',
                      boxShadow: '0 18px 60px rgba(0,0,0,0.65)',
                      color: '#e9e7ff',
                    },
                    userButtonPopoverMain: {
                      color: '#e9e7ff',
                    },
                    userButtonPopoverActionButton: {
                      color: '#e9e7ff',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.08)',
                      },
                    },
                    userButtonPopoverActionButton__manageAccount: {
                      color: '#e9e7ff',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.08)',
                      },
                    },
                    userButtonPopoverActionButton__signOut: {
                      color: '#e9e7ff',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.08)',
                      },
                    },
                    userButtonPopoverActionButtonText: {
                      color: '#e9e7ff',
                      fontWeight: 700,
                    },
                    userButtonPopoverActionButtonIcon: {
                      color: 'rgba(233,231,255,0.82)',
                    },
                    userButtonPopoverFooter: {
                      color: 'rgba(233,231,255,0.70)',
                    },
                  },
                }}
              />
            }
          />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              // Transparent so CssBaseline body gradients stay visible (Projects + full-width layouts were flat black).
              backgroundColor: 'transparent',
              overflow: 'auto',
            }}
          >
            <Routes>
              <Route
                path="/projects"
                element={
                  <ProjectsDashboard
                    onOpenProject={(projectId) => {
                      setCurrentProjectId(projectId);
                      navigate('/wallets');
                    }}
                  />
                }
              />
              <Route path="/launch-wizard" element={<LaunchWizard />} />
              <Route path="/launch-planner" element={<TokenDistributionCalculator />} />
              <Route path="/wallets" element={<WalletManagement />} />
              <Route path="/lp-locker" element={<LpLocker />} />
              <Route path="/bundle-launch" element={<TradingOperations />} />
              <Route path="/token-operations" element={<TokenOperations />} />
              <Route path="/backup" element={<Recovery />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/settings" element={<Navigate to="/wallets" replace />} />
              <Route path="/" element={<Navigate to="/projects" replace />} />
              <Route path="*" element={<Navigate to="/projects" replace />} />
            </Routes>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <BrowserRouter>
        <SignedOut>
          <Routes>
            <Route
              path="/admin/*"
              element={
                <AdminShell>
                  <AdminPanel />
                </AdminShell>
              }
            />
            <Route
              path="*"
              element={
                signedOutView.screen === 'auth' ? (
                  <AuthScreen
                    initialMode={signedOutView.authMode}
                    onBack={() => setSignedOutView({ screen: 'landing', authMode: 'signIn' })}
                  />
                ) : (
                  <LandingPage
                    onSignIn={() => setSignedOutView({ screen: 'auth', authMode: 'signIn' })}
                    onSignUp={() => setSignedOutView({ screen: 'auth', authMode: 'signUp' })}
                  />
                )
              }
            />
          </Routes>
        </SignedOut>

        <SignedIn>
          <SignedInRoutes />
        </SignedIn>
      </BrowserRouter>
      <ToastContainer position="bottom-right" theme="dark" />
    </ThemeProvider>
  );
}

export default App; 